import React, { useMemo } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Button, Dialog } from '../../../components/ui';
import useProfileDeleteGuard from '../hooks/useProfileDeleteGuard.js';
import useProfileSx from '../hooks/useProfileSx.js';
import { PROFILE_DELETE_COPY } from '../utils/profileConstants.js';
import formatAssignedUserLabel from '../utils/formatAssignedUserLabel.js';

export default function ProfileDeleteDialog({
  open,
  profile,
  onClose,
  onConfirm,
  onViewAssignedUsers,
  canViewUsers = false,
  isDeleting = false,
}) {
  const styles = useProfileSx();
  const { guard, isLoading, error } = useProfileDeleteGuard(profile?.id, open);

  const isBlocked = !isLoading && !error && !guard.canDelete;
  const canConfirmDelete = !isLoading && !error && guard.canDelete;

  const remainingUsersCount = useMemo(() => {
    if (!isBlocked) {
      return 0;
    }
    return Math.max(0, guard.assignedUsersCount - guard.assignedUsers.length);
  }, [guard.assignedUsers.length, guard.assignedUsersCount, isBlocked]);

  const dialogTitle = isBlocked
    ? PROFILE_DELETE_COPY.blockedTitle
    : PROFILE_DELETE_COPY.confirmTitle;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          {isBlocked && canViewUsers ? (
            <Button
              variant="primary"
              onClick={() => onViewAssignedUsers?.(profile)}
            >
              {PROFILE_DELETE_COPY.viewAssignedUsers}
            </Button>
          ) : null}
          {canConfirmDelete ? (
            <Button
              variant="destructive"
              loading={isDeleting}
              onClick={onConfirm}
            >
              Delete
            </Button>
          ) : null}
        </>
      }
    >
      {isLoading ? (
        <Box sx={styles.deleteGuardLoading}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {PROFILE_DELETE_COPY.guardLoading}
          </Typography>
        </Box>
      ) : null}

      {!isLoading && error ? (
        <Alert severity="warning" sx={styles.deleteGuardAlert}>
          {PROFILE_DELETE_COPY.guardError}
        </Alert>
      ) : null}

      {!isLoading && !error && isBlocked ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography>
            <strong>{profile?.name || 'This profile'}</strong>{' '}
            {PROFILE_DELETE_COPY.blockedIntro}{' '}
            <strong>{guard.assignedUsersCount}</strong>{' '}
            {guard.assignedUsersCount === 1 ? 'user' : 'users'}:
          </Typography>
          <Box component="ul" sx={styles.deleteAssignedUsersList}>
            {guard.assignedUsers.map((user) => (
              <Box component="li" key={user.id}>
                {formatAssignedUserLabel(user)}
              </Box>
            ))}
            {remainingUsersCount > 0 ? (
              <Box component="li">
                {PROFILE_DELETE_COPY.moreUsersLabel(remainingUsersCount)}
              </Box>
            ) : null}
          </Box>
          <Typography sx={styles.clonePreviewDescription}>
            {canViewUsers
              ? PROFILE_DELETE_COPY.blockedSuffix
              : PROFILE_DELETE_COPY.blockedNoUserAccess}
          </Typography>
        </Box>
      ) : null}

      {!isLoading && !error && canConfirmDelete ? (
        <Typography>
          {PROFILE_DELETE_COPY.confirmMessage}{' '}
          <strong>{profile?.name || 'this profile'}</strong>?{' '}
          {PROFILE_DELETE_COPY.confirmSuffix}
        </Typography>
      ) : null}
    </Dialog>
  );
}
